/** moderator/moderation — API. Proxied through portal-backend to the game
 * server, which answers in its own snake_case (types.ts: the *Wire shapes).
 * The match list and a player's details are read into the portal's shapes
 * here, once (helpers.ts). Signatures are positional to match call sites. */
import { http } from '../../_shared/http';
import { parseMatchList, parsePlayerDetails } from './helpers';
import type {
  BanPlayerWire,
  EndPrivateChatWire,
  Game,
  ModerationPlayerDetailsWire,
  PlayerDetails,
  ResolveHelpWire,
  StartPrivateChatWire,
} from './types';

export const moderationApi = {
  /** Every match the game server runs; fails on an answer that is not the list. */
  listMatches: (): Promise<readonly Game[]> => http.get<unknown>('/admin/moderation/matches').then(parseMatchList),

  voiceMonitorToken: (scope: { matchId: string; cohortId: string }) =>
    http.post<{ token: string; serverUrl: string; roomName: string; participantName: string }>('/admin/moderation/voice-monitor-token', scope),

  getPlayerDetails: (customId: string): Promise<PlayerDetails> =>
    http.post<ModerationPlayerDetailsWire>('/admin/moderation/player-details', { customId }).then(parsePlayerDetails),

  // A ban and a private call are by the signed-in account: portal-backend
  // reads the moderator from the session, so no request names them.
  banPlayer: (playerCustomId: string, matchId: string, reason: string) =>
    http.post<BanPlayerWire>('/admin/moderation/ban-player', { playerCustomId, matchId, reason }),

  startPrivateChat: (playerCustomId: string, gameId: string, worldId: string, cohortId: string, matchId: string) =>
    http.post<StartPrivateChatWire>('/admin/moderation/private-chat/start', {
      playerCustomId, gameId, worldId, cohortId, matchId,
    }),

  endPrivateChat: (playerCustomId: string, matchId: string, reason = 'ended') =>
    http.post<EndPrivateChatWire>('/admin/moderation/private-chat/end', { playerCustomId, matchId, reason }),

  resolveHelp: (userId: string, matchId: string) =>
    http.post<ResolveHelpWire>('/admin/moderation/resolve-help', { userId, matchId }),
};
